import { BrowserRouter, Routes, Route, NavLink, Link, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { BarChart3, ChevronRight, CircleDollarSign, Droplets, Beef, LayoutDashboard, Menu, Milk, Package, Receipt, Settings as SettingsIcon, Users, Wheat, X } from "lucide-react";
import Dashboard from "./pages/Dashboard";
import Rice from "./pages/Rice";
import Dairy from "./pages/Dairy";
import Sheep from "./pages/Sheep";
import Cattle from "./pages/Cattle";
import Employees from "./pages/Employees";
import Stock from "./pages/Stock";
import Finance from "./pages/Finance";
import Cheques from "./pages/Cheques";
import Reports from "./pages/Reports";
import Admin from "./pages/Admin";
import IOSInstallPrompt from "./components/farm/IOSInstallPrompt";
import AlertsBell from "./components/farm/AlertsBell";
import WeatherWidget from "./components/farm/WeatherWidget";
import { api } from "./lib/api";
import { registerSW } from "./lib/push";
import AccessGate, { getAccessRole } from "./components/farm/AccessGate";
import "@/App.css";

const NAV = [
  { to: "/", label: "Genel Bakış", icon: LayoutDashboard, end: true, key: "dashboard" },
  { to: "/rice", label: "Çeltik Üretimi", icon: Wheat, key: "rice" },
  { to: "/dairy", label: "Süt İşletmesi", icon: Droplets, key: "dairy" },
  { to: "/cattle", label: "Büyükbaş", icon: Milk, key: "cattle" },
  { to: "/sheep", label: "Küçükbaş", icon: Beef, key: "sheep" },
  { to: "/employees", label: "Çalışanlar", icon: Users, key: "employees" },
  { to: "/stock", label: "Stoklar", icon: Package, key: "stock" },
  { to: "/cheques", label: "Çekler", icon: Receipt, key: "cheques" },
  { to: "/finance", label: "Kasa · Finans", icon: CircleDollarSign, key: "finance" },
  { to: "/reports", label: "Raporlar", icon: BarChart3, key: "reports" },
  { to: "/admin", label: "Admin Paneli", icon: SettingsIcon, key: "admin" },
];

const LIMITED_KEYS = ["rice", "dairy", "cattle", "sheep"];

function Shell() {
  const [mobileNav, setMobileNav] = useState(false);
  const [settings, setSettings] = useState(null);
  const location = useLocation();
  const limited = getAccessRole() === "limited";
  const visibleNav = limited ? NAV.filter((n) => LIMITED_KEYS.includes(n.key)) : NAV;
  const current = visibleNav.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to))) || visibleNav[0];

  useEffect(() => {
    api.get("/settings").then(setSettings).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    registerSW();
  }, []);

  const ownerInit = (settings?.owner_name || "Faik Namlı").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const farmInit = (settings?.farm_name || "Kemal Namlı").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const guard = (key, el) => (limited && !LIMITED_KEYS.includes(key) ? <Navigate to="/dairy" replace /> : el);

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNav ? "open" : ""}`}>
        <div className="brand-hero">
          <button className="icon-btn mobile-close" data-testid="mobile-menu-close" onClick={() => setMobileNav(false)}>
            <X size={19} />
          </button>
          <div className="brand-logo-wrap">
            <div className="brand-logo-halo"></div>
            <div className="brand-logo">
              <img src="https://static.prod-images.emergentagent.com/jobs/fe9a795a-9b27-4bfd-9166-eea07adda0cf/images/de0f25fef2e7b6a99671adb5dfe2d7eadd23991c138ad11a61234e9051ce02e1.jpeg" alt="Kemal Namlı Çiftliği" />
            </div>
          </div>
          <div className="brand-title">
            <strong>{settings?.farm_name || "Kemal Namlı Çiftliği"}</strong>
            <span>{settings?.total_donum || 700} DÖNÜM · {(settings?.location || "Bafra / Samsun").toUpperCase()}</span>
          </div>
        </div>

        <div className="farm-switch">
          <div className="farm-avatar">{limited ? "SB" : farmInit}</div>
          <div>
            <b>{limited ? "Salih Bey" : "Faik Namlı"}</b>
            {!limited && <small>Çiftlik sahibi · Yönetici</small>}
          </div>
          <ChevronRight size={16} />
        </div>

        <nav>
          {visibleNav.map((n) => (
            <NavLink
              key={n.key}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.key}`}
              className={({ isActive }) => (isActive ? "active" : "")}
              onClick={() => setMobileNav(false)}
            >
              <n.icon size={18} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-bottom">
          <WeatherWidget />
          {limited ? (
            <div className="profile" data-testid="profile-limited">
              <div className="avatar">SB</div>
              <div>
                <b>Salih Bey</b>
              </div>
            </div>
          ) : (
            <div className="profile">
              <div className="avatar">{ownerInit}</div>
              <div>
                <b>{settings?.owner_name || "Faik Namlı"}</b>
                <small>Çiftlik sahibi</small>
              </div>
              <Link to="/admin" className="icon-btn" data-testid="profile-menu-button" title="Admin paneli">
                <SettingsIcon size={16} />
              </Link>
            </div>
          )}
        </div>
      </aside>

      <main className="main-content">
        <header className="topbar">
          <button className="icon-btn mobile-menu" data-testid="mobile-menu-button" onClick={() => setMobileNav(true)}>
            <Menu size={21} />
          </button>
          <div className="breadcrumbs">
            <span>Çiftliğim</span>
            <ChevronRight size={14} />
            <b>{current.label}</b>
          </div>
          <div className="top-actions">
            <AlertsBell />
          </div>
        </header>

        <div className="page-wrap">
          <Routes>
            <Route path="/" element={guard("dashboard", <Dashboard />)} />
            <Route path="/rice" element={guard("rice", <Rice />)} />
            <Route path="/dairy" element={<Dairy />} />
            <Route path="/sheep" element={<Sheep />} />
            <Route path="/cattle" element={<Cattle />} />
            <Route path="/employees" element={guard("employees", <Employees />)} />
            <Route path="/stock" element={guard("stock", <Stock />)} />
            <Route path="/finance" element={guard("finance", <Finance />)} />
            <Route path="/cheques" element={guard("cheques", <Cheques />)} />
            <Route path="/reports" element={guard("reports", <Reports />)} />
            <Route path="/admin" element={guard("admin", <Admin />)} />
            <Route path="*" element={<Navigate to={limited ? "/dairy" : "/"} replace />} />
          </Routes>
        </div>
      </main>
      <IOSInstallPrompt />
    </div>
  );
}

export default function App() {
  return (
    <AccessGate>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </AccessGate>
  );
}
